import './tailwind.css';

import { useSWEffect } from '@remix-pwa/sw';
import type { LinksFunction, LoaderFunction, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import type { ClientLoaderFunctionArgs, ShouldRevalidateFunction } from '@remix-run/react';
import { Outlet, isRouteErrorResponse, useRouteError, useSearchParams } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import { withSentry } from '@sentry/remix';
import { Toaster } from 'react-hot-toast';

import Document from '~/components/Document';
import FallBack from '~/components/Fallback';

import useCreateVisitToken from '~/hooks/useLoaders/useCreateVisitToken';
import useNotifications from '~/hooks/useNotifications';
import useCookies from '~/hooks/useUtils/useCookies';
import remixI18n from '~/i18next.server';
import { loaderSession } from '~/session';
import AdminBadge from './components/AdminBadge';
import { getAppGlobals } from './components/AppGlobals/utils';
import BeehiivHoney from './components/CookieBanner';
import EnablePushNotificationPrompt from './components/PWA/EnablePushNotificationPrompt';
import InstallPrompt from './components/PWA/InstallPrompt';
import HumanEmbed from './components/Scripts/HumanEmbed';
import { PageProvider } from './context/PageProvider';
import { env } from './env.server';
import type { ScriptRegistration } from './global';
import { usePushNotification } from './hooks/usePushNotification';
import useRemoveDraftPreviewCookie from './hooks/useRemoveDraftPreviewCookie';
import useJSDisabledSubmissionToasts from './hooks/useSubmissionErrorToast';
import usePWAReloadCheck from './hooks/useUtils/usePWAReloadCheck';
import useSiteLanguage from './hooks/useUtils/useSiteLanguage';
import useTypedLoaderData from './hooks/useUtils/useTypedLoaderData';
import useUserStatus from './hooks/useUtils/useUserStatus';
import type { Admin } from './interfaces/admin';
import type { Publication } from './interfaces/publication';
import type { Site } from './interfaces/site';
import type { Subscription } from './interfaces/subscriptions';
import getSite from './services/api/sites/getSite.server';
import getWebTheme from './services/api/web_theme/getWebTheme.server';
import getCookieValue from './utils/getCookieValue';
import isUserAgentMobile from './utils/isUserAgentMobile';
import redirectWithToast from './utils/redirectWithToast/index.server';
import { shouldRevalidateLoaderData } from './utils/shouldRevalidateLoaderData';

import { usePWAManager } from '@remix-pwa/client';
import { useEffect } from 'react';
import { ClientOnly } from 'remix-utils/client-only';
import { BirdieLoader } from '~/components/BirdieLoader';
import useRemoveParams from './hooks/useRemoveParams';
import useSetLastViewedResource from './hooks/useSetLastViewedResource';
import { useWorkerState } from './hooks/useWorkerState';
import { handleRedirect } from './utils/handleRedirect';
import { setCookieToken } from './utils/setTokenCookie/setCookieToken';

declare global {
  interface Window {
    beehiiv_registerScript: (script: string) => void;
    beehiiv_scriptsAfterHydration: Map<string /* url */, Map<string /* id/hash */, ScriptRegistration>>;
  }
}

const appGlobals = getAppGlobals();

export const links: LinksFunction = () => {
  return [
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com' },
  ];
};

export const handle = {
  // In the handle export, we could add a i18n key with namespaces our route
  // will need to load. This key can be a single string or an array of strings.
  i18n: ['common'],
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data?.publication || !data?.site) {
    return [
      { title: 'beehiiv' },
      { description: 'The newsletter platform built for growth.' },
      { keywords: 'Newsletter, Email, Growth, Platform, SaaS, Creator' },
    ];
  }

  const { publication, site } = data;
  const { tags, email_sender_name, logo, rss_feeds } = publication;
  const keywords = tags.map((tag: { name: string }) => tag.name).join(', ');

  const settingsMetadata = site?.viewable_site_version?.metadata;

  const metaData: Array<Record<string, string>> = [
    { title: settingsMetadata?.meta_title || '' },
    { description: settingsMetadata?.description || '' },
    { keywords },
    { author: email_sender_name || '' },
    {
      tagName: 'link',
      rel: 'icon',
      href: logo?.thumb?.url || '',
      as: 'image',
    },
  ];

  if (settingsMetadata?.noindex_enabled) {
    metaData.push({ robots: 'noindex' });
  }

  if (rss_feeds?.length) {
    metaData.push({
      tagName: 'link',
      rel: 'alternate',
      type: 'application/rss+xml',
      title: `${publication.name} RSS Feed`,
      href: rss_feeds[0].url || '',
    });
  }

  return metaData;
};

export interface RootLoaderData {
  site: Site;
  publication: Publication;
  recommendingPublication: Pick<Publication, 'id' | 'name'>;
  rootStyles: string;
  isLoggedIn: boolean;
  isFetchingSubscription: boolean;
  subscription: Subscription;
  admin: Admin;
  isProdEnv: boolean;
  facebookAppId: string;
  currentPath: string;
  showLoggedInToast: boolean;
  isStagingPreview: boolean;
  SENTRY_RELEASE: string;
  isUAStringMobile: boolean;
  isPostsPage: boolean;
  retry_fetch_enabled: boolean;
  noScript: boolean;
}

export const loader: LoaderFunction = async ({ request }) => {
  const { session } = await loaderSession(request);
  const url = new URL(request.url);
  const searchParams = url.searchParams;

  // If the request has cache buster, remove it and redirect without cache
  if (searchParams.has('__cb')) {
    searchParams.delete('__cb');
    throw redirect(`${url.pathname}${url.search}`, {
      headers: { 'cache-control': 'no-store' },
    });
  }
  const noScript = searchParams.get('no_script') === 'true';

  const [site, webTheme] = await Promise.all([getSite(request, session), getWebTheme(request, session)]);
  const { publication } = site;

  if (site.site_redirects_enabled) {
    handleRedirect(request, site.site_redirects || []);
  }

  Sentry.setContext('publication', {
    id: publication.id,
    name: publication.name,
  });

  const locale = publication?.language || 'en';
  const t = await remixI18n.getFixedT(locale, 'common');

  if (url.searchParams.get('upgrade') === 'success') {
    return redirect(
      redirectWithToast({
        baseUrl: '/',
        toast: 'success',
        title: t('upgrade.success.title') || '',
        message: t('upgrade.success.message'),
      }),
    );
  }

  // Check if the request host is different than the custom domain configured
  //   for the current publication (if any). If it is, we will redirect to the
  //   custom domain, retaining any parameters. This is useful for redirecting
  //   beehiiv publication subdomains to custom domains.
  //
  //   Example: get mypublication.beehiiv.com/p/test?foo=bar
  //     => 301 https://blog.mypublication.com/p/test?foo=bar
  // Make sure to strip the port from the host name if present
  const hostName = (request.headers.get('x-forwarded-host') || url.hostname).split(':')[0];

  const configuredDomain = publication.configured_domain;
  if (configuredDomain && configuredDomain !== hostName) {
    url.protocol = 'https:'; // Ensure the protocol is https
    url.hostname = configuredDomain;
    throw redirect(url.href, {
      status: 301,
    });
  }
  const cookieHeader = request.headers.get('Cookie');
  const isStagingPreview = cookieHeader?.includes('v2-preview');

  // Upgrade page redirect: If user's paid subscriptions are disabled, redirect back to home with a toast
  const isUpgradePage = url.pathname?.startsWith('/upgrade');
  if (!publication?.premium_enabled && isUpgradePage) {
    return redirect(
      redirectWithToast({
        baseUrl: '/',
        toast: 'error',
        message: t('upgrade.error.message'),
      }),
      307,
    );
  }

  const userAgent = request.headers.get('user-agent') || '';
  const isUAStringMobile = isUserAgentMobile(userAgent);

  return json({
    site,
    // TODO: Remove publication from root loader data since it is in site object
    publication,
    isLoggedIn: false,
    isFetchingSubscription: true,
    rootStyles: webTheme?.root_styles,
    admin: null,
    subscription: null,
    isProdEnv: process.env.NODE_ENV === 'production',
    facebookAppId: env.FACEBOOK_APP_ID,
    currentPath: url.pathname,
    showLoggedInToast: false,
    hostName,
    isStagingPreview,
    // include latest release in root to compare with initial document request
    // in AppGlobals. If different, for a refresh
    SENTRY_RELEASE: appGlobals.SENTRY_RELEASE,
    isUAStringMobile,
    retry_fetch_enabled: site.retry_fetch_enabled,
    noScript,
  });
};

export const clientLoader = async ({ request, serverLoader }: ClientLoaderFunctionArgs) => {
  const serverData = await serverLoader<RootLoaderData>();

  try {
    const url = new URL(request.url);
    const { publication } = serverData;

    const showLoggedInToast = url.searchParams.get('jwt_token');
    const token = url.searchParams.get('jwt_token') || getCookieValue('token', document.cookie);
    const adminToken = url.searchParams.get('admin_token') || getCookieValue('admin_token', document.cookie);
    const requests = [];

    if (token) {
      const requestBody: { jwt_token: string } = { jwt_token: token };

      requests.push(
        fetch('/current_subscription', {
          method: 'POST',
          headers: {
            'Sec-Fetch-Dest': 'empty',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }).then((res) => {
          if (res.ok) {
            document.cookie = setCookieToken({ key: 'token', token, isProdEnv: serverData?.isProdEnv });
          }
          return res;
        }),
      );
    } else {
      requests.push(Promise.resolve(null));
    }

    if (adminToken) {
      requests.push(
        fetch('/current_admin', {
          method: 'POST',
          headers: {
            'Sec-Fetch-Dest': 'empty',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ admin_token: adminToken }),
        }).then((res) => {
          if (res.ok) {
            document.cookie = setCookieToken({
              key: 'admin_token',
              token: adminToken,
              isProdEnv: serverData?.isProdEnv,
            });
          }
          return res;
        }),
      );
    } else {
      requests.push(Promise.resolve(null));
    }

    const [subscriptionRes, adminRes] = await Promise.all(requests);
    const [subscription, admin] = await Promise.all([
      subscriptionRes?.ok ? subscriptionRes.json() : null,
      adminRes?.ok ? adminRes.json() : null,
    ]);

    // PrivatePubs redirect: if publication is private or requires approval and user isn't subscribed
    const isPrivate = serverData.site?.publication?.private;
    const requireSubscriberApproval = serverData.site?.publication?.require_subscriber_approval;
    const shouldRedirect = !subscription && (isPrivate || requireSubscriberApproval);
    const isPostsPage = url.pathname?.startsWith('/p/');
    const isDraftPost = url.searchParams.get('draft') === 'true';

    if (shouldRedirect && isPostsPage && !isDraftPost) {
      window.location.href = '/';
      return serverData;
    }

    // Login page redirect: This specific redirect is for the /login route and checks if the user is already logged in
    const isUserLoggedIn = Boolean(subscription);
    const isLoginPage = url.pathname?.startsWith('/login');
    if (isUserLoggedIn && isLoginPage) {
      window.location.href = '/';
      return serverData;
    }

    if (subscription) {
      Sentry.setContext('subscription', {
        id: subscription.id,
        tier: subscription.tier,
        status: subscription.status,
        email: subscription.email,
        subscriberId: subscription.subscriber_id,
      });
    }

    if (!subscription && publication.require_subscriber_approval && window.location.pathname !== '/' && !isLoginPage) {
      window.location.href = '/';
    }

    // Remove any tokens from the url if they are present
    if (url.searchParams.has('jwt_token') || url.searchParams.has('admin_token')) {
      url.searchParams.delete('jwt_token');
      url.searchParams.delete('admin_token');
      window.history.replaceState({}, document.title, url.pathname + url.search);
    }

    return {
      ...serverData,
      isLoggedIn: !!subscription,
      admin: admin ?? null,
      isFetchingSubscription: false,
      subscription,
      showLoggedInToast: !!(showLoggedInToast && subscription),
      isPostsPage,
    };
  } catch (_error) {
    Sentry.captureException(_error);

    return {
      ...serverData,
      isLoggedIn: false,
      admin: null,
      subscription: null,
      isFetchingSubscription: false,
    };
  }
};

clientLoader.hydrate = true;

const App = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    site,
    isLoggedIn,
    subscription,
    admin,
    publication,
    rootStyles,
    isProdEnv,
    currentPath,
    showLoggedInToast,
    isStagingPreview,
    SENTRY_RELEASE,
    retry_fetch_enabled,
    isFetchingSubscription,
  } = useTypedLoaderData<RootLoaderData>();
  useSWEffect();
  const { swRegistration } = usePWAManager();
  useWorkerState(
    swRegistration?.active,
    (worker) => {
      if (worker.state === 'activated') {
        worker.postMessage({ type: 'update_fetcher', payload: retry_fetch_enabled });
      }
    },
    [retry_fetch_enabled],
  );

  // [USER STATUS] Sets the user status based on the subscription tier and whether or not they are logged in
  const { isLoggedInOrSubscribed, isPremiumSubscription } = useUserStatus({
    isLoggedIn,
    subscriptionTier: subscription?.tier,
    subscriptionStatus: subscription?.status,
  });

  // [COOKIES] Sets the user-specific cookies
  // Note: Only set cookies client side when users are not in a session
  // The Set-cookie header server side will impact our ability to cache web pages for performance
  // *cookieSet* is a way of letting us know that app has mounted and we have retrieved all available cookies
  const { cookies, setCookie, cookiesSet, removeCookie } = useCookies({
    initialCookies: {
      popup_closed: isLoggedInOrSubscribed,
      email_capture_disabled: isLoggedInOrSubscribed,
      active_upgrade: isPremiumSubscription,
    },
  });
  useRemoveDraftPreviewCookie();

  // [LANGUAGE] Sets the language for the site based on the publication language
  useSiteLanguage({ language: publication?.language, setCookie });

  // [VISIT TOKEN] Creates a visit token for the user
  // We use visit tokens to track user site visits + page views and more
  // It is critical to many parts of our web analytics that this is set before signups and page views
  const visitTokenCookie = Boolean(cookies.visit_token);
  useCreateVisitToken({
    shouldCreateVisitToken: cookiesSet && !visitTokenCookie,
    setCookie,
  });

  useSetLastViewedResource({ setCookie, cookiesSet });

  // [REMOVE PARAMS] Removes query params from the url
  // We occasionally user query params to pass state between pages
  // To prevent params from impacting state more than once, clear them from the url
  useRemoveParams(['status', 'upgraded']);

  // [NOTIFICATIONS] Handles the notifications for the site
  useJSDisabledSubmissionToasts();
  useNotifications({ showLoggedInToast: isLoggedIn && showLoggedInToast });
  usePushNotification({ icon: publication?.logo?.thumb?.url });

  usePWAReloadCheck({ sentryRelease: SENTRY_RELEASE });

  useEffect(() => {
    if (!isFetchingSubscription && (searchParams.get('jwt_token') || searchParams.get('admin_token'))) {
      searchParams.delete('jwt_token');
      searchParams.delete('admin_token');
      setSearchParams(searchParams, { preventScrollReset: true });
    }
  }, [searchParams, isFetchingSubscription]);

  return (
    <Document site={site} publication={publication} rootStyles={rootStyles} isProdEnv={isProdEnv} cookies={cookies}>
      <HumanEmbed />
      <PageProvider
        cookies={cookies}
        setCookie={setCookie}
        cookiesSet={cookiesSet}
        removeCookie={removeCookie}
        recaptchaSiteKey={appGlobals.CLOUDFLARE_TURNSTILE_SITE_KEY}
        visitToken={cookies.visit_token}
        referralId={cookies.referral_id}
        currentPath={currentPath}
        subscription={subscription}
        isStagingPreview={isStagingPreview}
      >
        <ClientOnly>{() => <BirdieLoader publication={publication} />}</ClientOnly>
        {admin && (
          <AdminBadge setCookie={setCookie} justLoggedIn={cookiesSet && !cookies.admin_logged_in && Boolean(admin)} />
        )}
        <Toaster position="top-center" />
        <BeehiivHoney
          isCookieBannerEnabled={site?.viewable_site_version?.settings?.cookie_banner_enabled}
          cookies={cookies}
          setCookie={setCookie}
          privacyPolicyUrl={site?.viewable_site_version?.settings?.privacy_policy_url}
          termsOfUseUrl={site?.viewable_site_version?.settings?.terms_of_use_url}
        />
        <Outlet
          context={{
            publicationId: publication?.id,
            publicationBeehiivBranding: publication?.beehiiv_branding,
            publicationName: publication?.name,
            publicationCopyrightText: publication?.copyright_text,
            recaptchaSiteKey: appGlobals.CLOUDFLARE_TURNSTILE_SITE_KEY,
            cookies,
            setCookie,
            cookiesSet,
            removeCookie,
            visitToken: cookies.visit_token,
            referralId: cookies.referral_id,
            admin,
            isAdmin: Boolean(admin),
            isLoggedIn,
          }}
        />
        {publication?.web_template?.pwa_enabled && (
          <>
            <InstallPrompt />
            <EnablePushNotificationPrompt />
          </>
        )}
      </PageProvider>
    </Document>
  );
};

const SentryApp = withSentry(App, {
  errorBoundaryOptions: {
    fallback: <FallBack errorCode="500" />,
  },
});
export default SentryApp;

export function ErrorBoundary() {
  const error = useRouteError();
  if (process.env.NODE_ENV !== 'development') {
    Sentry.captureRemixErrorBoundaryError(error);
  }

  if (isRouteErrorResponse(error)) {
    switch (error.status) {
      case 400: {
        return (
          <Document>
            <FallBack errorCode="400" publicationPresent={false} />
          </Document>
        );
      }
      case 404: {
        return (
          <Document>
            {error.data === 'DirectLinkExpired' ? (
              <FallBack
                customTitle={'Uh oh, it looks like this link is expired.'}
                customDescription={<>The Publication needs to update a few things on their end to resume.</>}
                errorCode="404"
                publicationPresent={false}
              />
            ) : (
              <FallBack errorCode="404" publicationPresent={false} />
            )}
          </Document>
        );
      }
      default: {
        return (
          <Document>
            <FallBack errorCode={`${error.status}`} error={error} />
          </Document>
        );
      }
    }
  }

  // default to client error
  let errorCode = '0';
  if (error instanceof Error) {
    let errorMessage = error.message ?? '';
    // if error message is a json string, parse it and extract status and message
    // this is set in `handleError` function in entry.server.tsx
    if (errorMessage.startsWith('{')) {
      try {
        const errorJson = JSON.parse(errorMessage);
        errorCode = errorJson.status ?? '0';
        errorMessage = errorJson.message ?? '';
      } catch (_e) {
        // do nothing
      }
    }
  }

  // To make debugging easier in development.
  if (process.env.NODE_ENV === 'development') {
    // biome-ignore lint/suspicious/noExplicitAny: Keeping for backward compatibility
    const unknownError: any = error;

    return (
      <Document title={unknownError?.name}>
        <div className="p-6 m-6 space-y-4 overflow-scroll text-sm text-red-600 rounded bg-red-50">
          <h1 className="text-lg font-semibold">Exception caught by root error boundary</h1>
          <p>{unknownError?.message}</p>
          <pre>{unknownError?.stack}</pre>
        </div>
      </Document>
    );
  }

  return (
    <Document title="Error!">
      <FallBack errorCode={errorCode} error={error} />
    </Document>
  );
}

export const shouldRevalidate: ShouldRevalidateFunction = shouldRevalidateLoaderData;
